import { formatARS, PAYMENT_METHOD_LABELS, PAYMENT_METHODS, type PaymentMethod } from '@gl/shared';
import { Banknote, CreditCard, Landmark, Minus, Plus, QrCode, ShoppingCart, Trash2, Wallet, type LucideIcon } from 'lucide-react';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { Cart } from './cart';
import { MoneyInput } from './MoneyInput';

export const PAYMENT_ICONS: Record<PaymentMethod, LucideIcon> = {
  EFECTIVO: Banknote,
  DEBITO: CreditCard,
  CREDITO: CreditCard,
  TRANSFERENCIA: Landmark,
  MERCADOPAGO: QrCode,
  OTRO: Wallet,
};

export function PaymentMethodPicker({
  value,
  onChange,
  className,
}: {
  value: PaymentMethod;
  onChange: (m: PaymentMethod) => void;
  className?: string;
}) {
  return (
    <div className={cn('grid grid-cols-3 gap-2', className)} role="radiogroup" aria-label="Método de pago">
      {PAYMENT_METHODS.map((m) => {
        const Icon = PAYMENT_ICONS[m];
        const active = value === m;
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(m)}
            className={cn(
              'flex min-h-14 flex-col items-center justify-center gap-1 rounded-xl border px-1 text-xs font-medium transition active:scale-[0.97]',
              active ? 'border-primary bg-primary/10 text-primary ring-1 ring-primary' : 'hover:bg-accent',
            )}
          >
            <Icon className="size-5" />
            {PAYMENT_METHOD_LABELS[m]}
          </button>
        );
      })}
    </div>
  );
}

export function CartPanel({
  cart,
  paymentMethod,
  onPaymentMethod,
  onConfirm,
  pending,
  disabled,
}: {
  cart: Cart;
  paymentMethod: PaymentMethod;
  onPaymentMethod: (m: PaymentMethod) => void;
  onConfirm: () => void;
  pending: boolean;
  disabled: boolean;
}) {
  const empty = cart.lines.length === 0;
  const discountTooHigh = cart.discountCents > cart.subtotalCents;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {empty ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
            <ShoppingCart className="size-8 opacity-40" />
            Tocá un producto para agregarlo.
          </div>
        ) : (
          <ul className="divide-y">
            {cart.lines.map((l) => (
              <li key={l.key} className="flex items-center gap-2 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {l.name}
                    {l.variantName && <span className="text-muted-foreground"> · {l.variantName}</span>}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {formatARS(l.unitPriceCents)} × {l.quantity} = {formatARS(l.unitPriceCents * l.quantity)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-9"
                    aria-label={l.quantity === 1 ? 'Quitar' : 'Uno menos'}
                    onClick={() => cart.dispatch({ type: 'qty', key: l.key, delta: -1 })}
                  >
                    {l.quantity === 1 ? <Trash2 className="size-4" /> : <Minus className="size-4" />}
                  </Button>
                  <span className="w-7 text-center text-sm font-semibold tabular-nums">{l.quantity}</span>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="size-9"
                    aria-label="Uno más"
                    onClick={() => cart.dispatch({ type: 'qty', key: l.key, delta: 1 })}
                  >
                    <Plus className="size-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid gap-3 border-t pt-3">
        <div className="flex items-center gap-3">
          <Label htmlFor="cart-discount" className="shrink-0 text-sm">
            Descuento
          </Label>
          <div className="flex-1">
            <MoneyInput
              id="cart-discount"
              value={cart.discountCents || null}
              onChange={(c) => cart.dispatch({ type: 'discount', cents: c ?? 0 })}
              placeholder="0"
              disabled={empty}
              aria-invalid={discountTooHigh}
            />
          </div>
        </div>
        {discountTooHigh && <p className="text-xs text-destructive">El descuento supera el subtotal.</p>}

        <PaymentMethodPicker value={paymentMethod} onChange={onPaymentMethod} />

        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted-foreground">
            {cart.count} {cart.count === 1 ? 'producto' : 'productos'}
            {cart.discountCents > 0 && ` · subtotal ${formatARS(cart.subtotalCents)}`}
          </span>
          <span className="text-2xl font-bold tabular-nums">{formatARS(cart.totalCents)}</span>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="h-14"
            disabled={empty || pending}
            onClick={() => cart.dispatch({ type: 'clear' })}
            aria-label="Vaciar carrito"
          >
            <Trash2 />
          </Button>
          <Button
            type="button"
            size="lg"
            className="h-14 flex-1 text-base"
            disabled={empty || pending || disabled || discountTooHigh}
            onClick={onConfirm}
          >
            {pending && <Spinner />} Cobrar {formatARS(cart.totalCents)}
          </Button>
        </div>
      </div>
    </div>
  );
}
